import { Shift } from '@shared/types/shifts';
import { LEVEL_COLORS } from 'restaurantAdmin/utils/constants';
import type { UpdateTemporalOccupancyRatingsPayload } from '../apiHelpers';
import type {
  DayOfWeekOccupancyRating,
  MonthRating,
  PeakPersonalizations,
  TemporalOccupancyRatings,
  TimeOfDayOccupancyRating,
} from '../types';
import { MonthOccupancyRating, OccupancyRating } from '../types';
import { getEarliestStartTime, getLatestEndTime } from '@shared/utils/shifts';
import { eachMinuteOfInterval, format } from 'date-fns';
import { dateToISOTime } from '@shared/utils/time';

const API_RATING_MAX = OccupancyRating.Busiest;
const MONTH_API_RATING_MAX = MonthOccupancyRating.ExtremelyBusy;
const DISPLAY_MAX = 100;

const apiRatingToDisplay = (rating: number): number =>
  Math.round((rating / API_RATING_MAX) * DISPLAY_MAX);

const displayToApiRating = (heightPercent: number): number =>
  Math.min(
    API_RATING_MAX,
    Math.max(0, Math.round((heightPercent / DISPLAY_MAX) * API_RATING_MAX)),
  );

const monthApiRatingToDisplay = (rating: number): number =>
  Math.round((rating / MONTH_API_RATING_MAX) * DISPLAY_MAX);

const displayToMonthApiRating = (heightPercent: number): number =>
  Math.min(
    MONTH_API_RATING_MAX,
    Math.max(
      MonthOccupancyRating.Slowest,
      Math.round((heightPercent / DISPLAY_MAX) * MONTH_API_RATING_MAX),
    ),
  );

export const apiToWeeklyPeaks = (
  temporalOccupancyRatings: TemporalOccupancyRatings | null | undefined,
): number[] => {
  const dayRatings = temporalOccupancyRatings?.dayOfWeekOccupancyRatings ?? [];
  return [0, 1, 2, 3, 4, 5, 6].map((dayOfWeek) => {
    const rating = dayRatings.find((r) => r.dayOfWeek === dayOfWeek)?.rating;
    return rating !== undefined
      ? apiRatingToDisplay(rating)
      : apiRatingToDisplay(OccupancyRating.Slowest);
  });
};

export const apiToYearlyPeaks = (
  temporalOccupancyRatings: TemporalOccupancyRatings | null | undefined,
): number[] => {
  const monthRatings =
    temporalOccupancyRatings?.monthOfYearOccupancyRatings ?? [];
  return [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((month) => {
    const rating = monthRatings.find((r) => r.month === month)?.rating;
    return rating !== undefined
      ? monthApiRatingToDisplay(rating)
      : monthApiRatingToDisplay(MonthOccupancyRating.Slowest);
  });
};

export const apiToDailyPeaks = (
  temporalOccupancyRatings: TemporalOccupancyRatings | null | undefined,
): Omit<TimeOfDayOccupancyRating, 'id'>[] => {
  return (
    temporalOccupancyRatings?.timeOfDayOccupancyRatings?.map((occupancy) => ({
      ...occupancy,
      rating: apiRatingToDisplay(occupancy.rating),
    })) ?? []
  );
};

/**
 * Builds the API payload from weekly peaks (UI state) and existing temporal
 * occupancy ratings. Preserves time-of-day and month ratings from the API
 * response; translates only day-of-week ratings from the new UI format.
 */
const dailyPeaksToPayload = (
  dailyPeaks: Omit<TimeOfDayOccupancyRating, 'id'>[],
): Omit<TimeOfDayOccupancyRating, 'id'>[] => {
  const timeOfDayRatings =
    dailyPeaks.map(({ time, dayOfWeek, rating }) => ({
      time,
      dayOfWeek,
      rating: displayToApiRating(rating),
    })) ?? [];

  return timeOfDayRatings;
};

const weeklyPeaksToPayload = (
  weeklyPeaks: number[],
): Omit<DayOfWeekOccupancyRating, 'id'>[] => {
  const dayOfWeekRatings = weeklyPeaks.map((heightPercent, index) => ({
    dayOfWeek: index as 0 | 1 | 2 | 3 | 4 | 5 | 6,
    rating: displayToApiRating(heightPercent),
  }));

  return dayOfWeekRatings;
};

const yearlyPeaksToPayload = (yearlyPeaks: number[]): MonthRating[] => {
  const monthOfYearRatings = yearlyPeaks.map((heightPercent, index) => ({
    month: (index + 1) as 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12,
    rating: displayToMonthApiRating(heightPercent),
  }));

  return monthOfYearRatings;
};

export const peaksToPayload = (
  dailyPeaks: Omit<TimeOfDayOccupancyRating, 'id'>[],
  weeklyPeaks: number[],
  yearlyPeaks: number[],
): UpdateTemporalOccupancyRatingsPayload => {
  const daily = dailyPeaksToPayload(dailyPeaks);
  const weekly = weeklyPeaksToPayload(weeklyPeaks);
  const yearly = yearlyPeaksToPayload(yearlyPeaks);
  return {
    timeOfDayOccupancyRatings: daily,
    dayOfWeekOccupancyRatings: weekly,
    monthOfYearOccupancyRatings: yearly,
  };
};

export const OCCUPANCY_RATING_VALUE_LEVELS = [
  OccupancyRating.Slowest,
  OccupancyRating.Slow,
  OccupancyRating.Normal,
  OccupancyRating.Busy,
  OccupancyRating.Busiest,
].map(apiRatingToDisplay);

export const MONTH_OCCUPANCY_RATING_VALUE_LEVELS = [
  MonthOccupancyRating.Slowest,
  MonthOccupancyRating.Slow,
  MonthOccupancyRating.Normal,
  MonthOccupancyRating.Busy,
  MonthOccupancyRating.Busier,
  MonthOccupancyRating.Busiest,
  MonthOccupancyRating.ExtremelyBusy,
].map(monthApiRatingToDisplay);

/**
 * Builds the column definitions for the daily peak personalization chart.
 */
export const buildTimeOfDayRatingColumnDefsDaily = (
  availabilityInterval: number,
  shifts: Shift[],
): { field: string; headerName: string }[] => {
  const start = getEarliestStartTime(shifts);
  const end = getLatestEndTime(shifts);

  return eachMinuteOfInterval(
    { start, end },
    { step: availabilityInterval },
  ).map((interval) => ({
    field: dateToISOTime(interval),
    headerName: format(interval, 'h:mm a').toLowerCase(),
  }));
};

export const getColorForPercent = (percent: number): string => {
  if (percent === 0) return 'transparent';

  let color = '';
  const levels = 100 / LEVEL_COLORS.length;

  for (let index = 0; index < LEVEL_COLORS.length; index++) {
    const level = LEVEL_COLORS[index];
    if (percent <= levels * (index + 1)) {
      color = level.color;
      break;
    }
  }

  return color;
};

export const validateHasUnsavedPeakChanges = (
  { dailyPeaks, weeklyPeaks, yearlyPeaks }: PeakPersonalizations,
  savedPeaks: PeakPersonalizations,
) => {
  const minValidValue = 0.001;

  const weeklyChanged =
    weeklyPeaks.length !== 7 ||
    weeklyPeaks.some(
      (v, i) => Math.abs(v - savedPeaks.weeklyPeaks[i]) > minValidValue,
    );
  const yearlyChanged =
    yearlyPeaks.length !== 12 ||
    yearlyPeaks.some(
      (v, i) => Math.abs(v - savedPeaks.yearlyPeaks[i]) > minValidValue,
    );

  const savedDailyMap = new Map(
    savedPeaks.dailyPeaks.map((p) => [`${p.dayOfWeek}-${p.time}`, p.rating]),
  );
  const currentDailyMap = new Map(
    dailyPeaks.map((p) => [`${p.dayOfWeek}-${p.time}`, p.rating]),
  );

  const allDailyKeys = new Set([
    ...savedDailyMap.keys(),
    ...currentDailyMap.keys(),
  ]);
  const hasDailyChanges = [...allDailyKeys].some((key) => {
    const saved = savedDailyMap.get(key) ?? 0;
    const current = currentDailyMap.get(key) ?? 0;
    return Math.abs(current - saved) > minValidValue;
  });

  return weeklyChanged || yearlyChanged || hasDailyChanges;
};
